import * as Sentry from "@sentry/browser";
import Environment from "../common/environment";

const filterErrorDomains = [
  "https://web-analytics.engagio.com/",
  "https://match.prod.bidr.io/",
  "https://id.rlcdn.com/",
  "https://platform.twitter.com/",
  "https://connect.facebook.net/",
  "https://cdnssl.clicktale.net/",
  "https://googleads.g.doubleclick.net/",
  "https://scripts.demandbase.com/",
  "https://munchkin.marketo.net/",
  "https://analytics.twitter.com/",
  "https://snap.licdn.com/",
  "https://www.googletagmanager.com/",
  "https://www.googleadservices.com/",
  "https://www.google-analytics.com/",
  "https://www.redditstatic.com/",
  "https://api.autopilothq.com/",
  "https://cdn.bizible.com/",
  "https://a.quora.com/",
  "https://www.google.com/",
];

const currentEnvironment = Environment.getEnvironment();

Sentry.init({
  // https://hello.atlassian.net/wiki/spaces/OBSERVABILITY/pages/1436386358/Sentry+Cloud+-+Getting+Started#Setting-up-the-Sentry-SDK-for-your-project
  dsn: "https://1c1d742244454a16842f0bf5a72c06a2@o55978.ingest.sentry.io/5988886", // Should match `.sentryclirc`
  environment: currentEnvironment,
  release: WEBPACK_SENTRY_BUILD_VERSION,
});

const shouldLogError = (errorSourceUrl: string) => {
  const errorUrlRootResult = /^https?:\/\/.*?\//.exec(errorSourceUrl);

  if (
    errorUrlRootResult &&
    filterErrorDomains.includes(errorUrlRootResult[0])
  ) {
    return false;
  }

  return true;
};

// Remove everything after characters `?` or `#` in a URL before logging.
const stripUrl = (url: string) => url.replace(/[?#].*$/i, "");

// See https://github.com/getsentry/sentry-javascript/issues/3382#issuecomment-817673194
const tagErrorHandler = (event: ErrorEvent) => {
  if (!event.target) {
    Sentry.captureMessage(
      `Error without target, type: ${event.type || "unknown"}`,
      Sentry.Severity.Error
    );
    return;
  }

  let errorTag = "undefined";
  let errorSourceUrl = "unknown";
  const errorSeverity = Sentry.Severity.Error;

  const targetElement = event.target as HTMLElement;

  if (targetElement && targetElement.tagName) {
    errorTag = `${targetElement.tagName}`;

    const tagNameLowercase = errorTag.toLowerCase();

    if (tagNameLowercase === "img") {
      errorSourceUrl = `${(targetElement as HTMLImageElement).src}`;
    } else if (tagNameLowercase === "link") {
      errorSourceUrl = `${(targetElement as HTMLLinkElement).href}`;
    } else if (tagNameLowercase === "script") {
      errorSourceUrl = `${(targetElement as HTMLScriptElement).src}`;
    }
  }

  if (!shouldLogError(errorSourceUrl)) {
    return;
  }

  errorSourceUrl = stripUrl(errorSourceUrl);

  Sentry.captureMessage(
    `Uncaught error event from "${errorTag}", source: ${errorSourceUrl}`,
    errorSeverity
  );
};

const attachTagErrorHandler = (element: HTMLElement | Document) => {
  element.addEventListener("error", tagErrorHandler as EventListener, true);
};

attachTagErrorHandler(document);

// Don't trigger attaching to document body until page readyState has left "loading"
// "loading" -> "interactive" -> "complete"
if (document.readyState !== "loading") {
  attachTagErrorHandler(document.body);
} else {
  window.addEventListener("DOMContentLoaded", () => {
    attachTagErrorHandler(document.body);
  });
}
